import type { TransferDirection, TransferIntegrity, TransferStatus } from '@flowshare/protocol';
import { STORAGE_KEYS } from '@flowshare/protocol';
import { create } from 'zustand';

export interface TransferRecord {
  id: string;
  direction: TransferDirection;
  peerId: string;
  peerName: string;
  fileName: string;
  fileSize: number;
  mimeType: string | null;
  status: TransferStatus;
  bytesTransferred: number;
  chunksTransferred: number;
  totalChunks: number;
  chunkSize: number;
  startedAt: number;
  updatedAt: number;
  completedAt: number | null;
  speedBps: number;
  error: string | null;
  integrity: TransferIntegrity;
  sha256: string | null;
  storageKey: string | null;
  requiresAcceptance: boolean;
  canRetry: boolean;
}

interface TransferStore {
  transfers: Record<string, TransferRecord>;
  upsert: (record: TransferRecord) => void;
  patch: (id: string, patch: Partial<TransferRecord>) => void;
  remove: (id: string) => void;
  clearFinished: () => void;
  reset: () => void;
}

export const isActiveTransfer = (record: TransferRecord): boolean =>
  record.status === 'waiting' ||
  record.status === 'connecting' ||
  record.status === 'transferring';

export const isFinishedTransfer = (record: TransferRecord): boolean =>
  record.status === 'completed' ||
  record.status === 'failed' ||
  record.status === 'cancelled';

const MAX_PERSISTED_RECORDS = 60;

/**
 * The transfer engine lives in memory, but the dashboard should survive an
 * accidental reload. We persist metadata only — never file bodies or File
 * handles — and mark anything that was mid-flight as interrupted.
 */
function restoreTransfers(): Record<string, TransferRecord> {
  if (typeof sessionStorage === 'undefined') return {};
  try {
    const raw = sessionStorage.getItem(STORAGE_KEYS.transfers);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') return {};

    const entries = Object.entries(parsed as Record<string, unknown>);
    const restored: Record<string, TransferRecord> = {};

    for (const [id, value] of entries.slice(0, MAX_PERSISTED_RECORDS)) {
      if (!value || typeof value !== 'object') continue;
      const record = value as Partial<TransferRecord>;
      if (
        typeof record.fileName !== 'string' ||
        typeof record.fileSize !== 'number' ||
        typeof record.startedAt !== 'number' ||
        (record.direction !== 'send' && record.direction !== 'receive')
      ) {
        continue;
      }

      const finished = isFinishedTransfer(record as TransferRecord);
      restored[id] = {
        ...(record as TransferRecord),
        status: finished ? (record.status as TransferStatus) : 'failed',
        error: finished ? (record.error ?? null) : 'Interrupted by a page reload.',
        canRetry: false,
        requiresAcceptance: false,
        speedBps: 0,
        completedAt: record.completedAt ?? Date.now(),
        updatedAt: Date.now(),
      };
    }

    return restored;
  } catch {
    return {};
  }
}

let persistTimer: ReturnType<typeof setTimeout> | null = null;

function schedulePersist(): void {
  if (persistTimer || typeof sessionStorage === 'undefined') return;
  persistTimer = setTimeout(() => {
    persistTimer = null;
    try {
      const records = Object.values(useTransferStore.getState().transfers)
        .sort((left, right) => right.startedAt - left.startedAt)
        .slice(0, MAX_PERSISTED_RECORDS);
      const payload: Record<string, TransferRecord> = {};
      for (const record of records) payload[record.id] = record;
      sessionStorage.setItem(STORAGE_KEYS.transfers, JSON.stringify(payload));
    } catch {
      // Session storage is best effort.
    }
  }, 800);
}

export const useTransferStore = create<TransferStore>()((set) => ({
  transfers: restoreTransfers(),
  upsert: (record) =>
    set((state) => ({ transfers: { ...state.transfers, [record.id]: record } })),
  patch: (id, patch) =>
    set((state) => {
      const existing = state.transfers[id];
      if (!existing) return state;
      return { transfers: { ...state.transfers, [id]: { ...existing, ...patch } } };
    }),
  remove: (id) =>
    set((state) => {
      const next = { ...state.transfers };
      delete next[id];
      return { transfers: next };
    }),
  clearFinished: () =>
    set((state) => {
      const next: Record<string, TransferRecord> = {};
      for (const [id, record] of Object.entries(state.transfers)) {
        if (!isFinishedTransfer(record)) next[id] = record;
      }
      return { transfers: next };
    }),
  reset: () => set({ transfers: {} }),
}));

useTransferStore.subscribe(() => schedulePersist());

export function sortedTransfers(transfers: Record<string, TransferRecord>): TransferRecord[] {
  return Object.values(transfers).sort((left, right) => right.startedAt - left.startedAt);
}
